import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { APP_COLORS as C, APP_FONT as F } from '../tokens';
import {
  VELA_APPROVALS,
  VELA_ACTIVITY,
  type VelaApproval,
  type VelaApprovalKind,
} from '../../../vela-inbox/data';

type Props = {
  // Kept for backwards compat with Dashboard/index.tsx; ignored internally.
  count?: number;
  summary?: string;
  onReviewAll?: () => void;
};

const VELA_GRADIENT = 'linear-gradient(135deg, #6B46C1 0%, #8B5CF6 100%)';

const EDGE: Record<VelaApprovalKind, string> = {
  urgent: '#DC2626',
  good: '#0F6E56',
  warn: '#CA8A04',
};

const TAG_STYLE: Record<VelaApprovalKind, { background: string; color: string }> = {
  urgent: { background: '#FEE2E2', color: '#DC2626' },
  good:   { background: '#DCFCE7', color: '#0F6E56' },
  warn:   { background: '#FEF3C7', color: '#CA8A04' },
};

function VChip({ count, large }: { count: number; large?: boolean }): React.ReactElement {
  const size = large ? 42 : 36;
  const svgSize = large ? 18 : 16;
  const badgeLabel = count > 9 ? '9+' : String(count);
  return (
    <span style={{ position: 'relative', flexShrink: 0, display: 'inline-flex' }}>
      <span style={{
        width: size, height: size,
        borderRadius: '50%',
        background: VELA_GRADIENT,
        display: 'grid', placeItems: 'center',
        boxShadow: '0 4px 12px rgba(107,70,193,0.3)',
      }}>
        <svg viewBox="0 0 24 24" width={svgSize} height={svgSize} fill="#fff" style={{ display: 'block' }}>
          <path d="M4 3 L10 20 L14 20 L20 3 L16 3 L12 15 L8 3 Z"/>
        </svg>
      </span>
      {count > 0 && (
        <span style={{
          position: 'absolute', top: -4, right: -4,
          background: '#DC2626', color: '#fff',
          fontSize: 10, fontWeight: 800,
          minWidth: 18, height: 18,
          borderRadius: 999,
          display: 'grid', placeItems: 'center',
          border: '2px solid #fff',
          padding: '0 5px',
        }}>{badgeLabel}</span>
      )}
    </span>
  );
}

function ApprovalCard({ a, onReview }: { a: VelaApproval; onReview: (href: string) => void }): React.ReactElement {
  return (
    <div style={{
      background: '#fff',
      border: '1px solid #E7EAF0',
      borderRadius: 10,
      padding: '11px 13px',
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
      position: 'relative',
      overflow: 'hidden',
      minWidth: 0,
    }}>
      <div style={{
        position: 'absolute', left: 0, top: 0, bottom: 0,
        width: 3, background: EDGE[a.kind],
      }}/>
      <span style={{
        fontSize: 9, fontWeight: 800,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        padding: '2px 7px',
        borderRadius: 999,
        width: 'fit-content',
        ...TAG_STYLE[a.kind],
      }}>{a.tag}</span>
      <div style={{ fontSize: 12, color: C.ink, lineHeight: 1.4, fontWeight: 500 }}>
        {a.title}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto', gap: 10 }}>
        <button
          onClick={() => onReview(a.reviewHref)}
          style={{
            background: C.ink, color: '#fff',
            border: 0, padding: '7px 12px',
            borderRadius: 8, fontSize: 11, fontWeight: 700,
            cursor: 'pointer', fontFamily: F.family,
          }}
        >{a.btnLabel} &rsaquo;</button>
        {a.skipLabel && (
          <span style={{ fontSize: 10, color: '#94A3B8', cursor: 'pointer' }}>
            {a.skipLabel}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Vela banner with three states:
 *  1. Empty  (0 approvals)   -> compact "Vela is watching"
 *  2. Collapsed (N approvals) -> compact "Vela has N things for you" + red count badge, click to expand
 *  3. Expanded                -> full card with approval cards + "Open Vela inbox" link + Collapse button
 */
export default function VelaPreparedHero(_props: Props): React.ReactElement {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const approvals = VELA_APPROVALS;
  const count = approvals.length;

  const summaryLine = approvals.slice(0, 3).map((a) => a.shortTag).join(' · ');

  const handleReview = (href: string) => {
    navigate(href);
  };

  // STATE 1: empty
  if (count === 0) {
    return (
      <div style={{
        maxWidth: 560,
        background: 'linear-gradient(135deg, #F3EFFC 0%, #FAF8FF 100%)',
        border: '1px solid #E7E0F5',
        borderRadius: 14,
        padding: '14px 18px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 14,
        position: 'relative',
        overflow: 'hidden',
        marginBottom: 20,
        fontFamily: F.family,
      }}>
        <div style={{
          position: 'absolute', left: 0, top: 0, bottom: 0,
          width: 3, background: VELA_GRADIENT,
        }}/>
        <VChip count={0}/>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.ink }}>
            Vela is watching
          </div>
          <div style={{ fontSize: 11.5, color: C.sub, marginTop: 2 }}>
            Drafts will appear here for your review.
          </div>
        </div>
      </div>
    );
  }

  // STATE 2: collapsed with count
  if (!isExpanded) {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={() => setIsExpanded(true)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setIsExpanded(true); }}
        style={{
          maxWidth: 560,
          background: 'linear-gradient(135deg, #F3EFFC 0%, #FAF8FF 100%)',
          border: '1px solid #E7E0F5',
          borderRadius: 14,
          padding: '14px 18px',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 14,
          position: 'relative',
          overflow: 'hidden',
          marginBottom: 20,
          fontFamily: F.family,
          cursor: 'pointer',
          transition: 'box-shadow 0.15s',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(107,70,193,0.15)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
      >
        <div style={{
          position: 'absolute', left: 0, top: 0, bottom: 0,
          width: 3, background: VELA_GRADIENT,
        }}/>
        <VChip count={count}/>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.ink }}>
            Vela has {count} {count === 1 ? 'thing' : 'things'} for you
          </div>
          <div style={{ fontSize: 11.5, color: C.sub, marginTop: 2 }}>
            {summaryLine}
          </div>
        </div>
        <span style={{
          color: '#6B46C1', fontSize: 16, fontWeight: 800,
          flexShrink: 0, marginLeft: 6,
        }}>&#9662;</span>
      </div>
    );
  }

  // STATE 3: expanded
  return (
    <div style={{
      background: 'linear-gradient(135deg, #F3EFFC 0%, #FAF8FF 60%, #FFFFFF 100%)',
      border: '1px solid #E7E0F5',
      borderRadius: 16,
      padding: '20px 22px',
      marginBottom: 20,
      position: 'relative',
      overflow: 'hidden',
      boxShadow: '0 2px 10px rgba(107,70,193,0.08)',
      fontFamily: F.family,
    }}>
      <div style={{
        position: 'absolute', left: 0, top: 0, bottom: 0,
        width: 4, background: VELA_GRADIENT,
      }}/>

      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12 }}>
        <VChip count={count} large/>
        <div style={{ flex: 1 }}>
          <h3 style={{
            fontSize: 17, fontWeight: 700, color: C.ink,
            margin: 0, letterSpacing: '-0.01em', lineHeight: 1.3,
          }}>
            I did {VELA_ACTIVITY.thingsDone} things since you were last here. {count} need your review.
          </h3>
        </div>
        <button
          onClick={() => setIsExpanded(false)}
          style={{
            background: '#fff',
            border: '1px solid #E7E0F5',
            color: '#6B46C1',
            fontSize: 11, fontWeight: 600,
            padding: '6px 12px',
            borderRadius: 8,
            cursor: 'pointer',
            flexShrink: 0,
            fontFamily: F.family,
          }}
        >Collapse &#9652;</button>
      </div>

      <p style={{ fontSize: 12.5, color: C.sub, margin: '0 0 14px', lineHeight: 1.5 }}>
        {VELA_ACTIVITY.summary}{' '}
        <b style={{ color: C.ink }}>Nothing sends or transfers until you review and confirm.</b>
      </p>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: 10,
      }}>
        {approvals.map((a) => (
          <ApprovalCard key={a.id} a={a} onReview={handleReview}/>
        ))}
      </div>

      <div style={{
        marginTop: 14,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: 10,
        borderTop: '1px solid #E7E0F5',
      }}>
        <span
          role="button"
          tabIndex={0}
          onClick={() => navigate('/vela/inbox')}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate('/vela/inbox'); }}
          style={{
            color: '#6B46C1', fontSize: 12, fontWeight: 700,
            cursor: 'pointer',
          }}
        >Open Vela inbox &rsaquo;</span>
        <span style={{ fontSize: 10, color: C.sub }}>Updated just now</span>
      </div>
    </div>
  );
}
